import CheckIcon from '@heroicons/react/outline/CheckIcon'
import ChevronDownIcon from '@heroicons/react/outline/ChevronDownIcon'
import ChevronUpIcon from '@heroicons/react/outline/ChevronUpIcon'
import SearchIcon from '@heroicons/react/outline/SearchIcon'
import XIcon from '@heroicons/react/solid/XIcon'
import clsx from 'clsx'
import InfiniteScroll from 'react-infinite-scroller'
import { Link as ReactLink } from 'react-router-dom'
import { useColumnOrder, useTable } from 'react-table'

import { HtmlContent } from '@tribeplatform/react-sdk/components'
import { useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Table as TableComponent } from '@tribeplatform/react-ui-kit/Table'

import { dayjs } from '../../lib/dayjs'
import { useScroll } from '../Admin/ScrollProvider'
import { SpaceImage } from '../Space/SpaceImage'
import { FieldsDropdown } from './FieldsDropdown'
import { FieldsFilter } from './FieldsFilter'

export const Table = ({
  columns,
  data,
  fetchNextPage,
  hasNextPage = false,
  isLoading = false,
  isFetchingNextPage = false,
  fieldsFooter = null,
  filters = { query: null, filterBy: [] },
  setFilters = null,
  orderBy = null,
  setOrderBy = null,
  title = '',
  actions = null,
  ...props
}) => {
  const { generateMemberPath, generatePostPath } = useSpaceUtil()
  const memoData = React.useMemo(() => data, [data]) || []
  const memoColumns = React.useMemo(() => columns, [columns]) || []
  const { nodes: items } = simplifyPaginatedResult(memoData)
  const totalCount = data?.pages?.[0].totalCount

  const { scrollRef } = useScroll()

  const CellRenderer = ({
    value,
    row: { values },
    column: { renderer, format },
  }) => {
    if (typeof renderer === 'function') {
      return renderer(values)
    }

    switch (renderer) {
      case 'memberPrimary':
        return (
          <Link
            className="hover:underline"
            as={ReactLink}
            to={generateMemberPath(values)}
          >
            {value}
          </Link>
        )
      case 'spacePrimary':
        return (
          <Link
            className="hover:underline"
            as={ReactLink}
            to={`/${values.slug}`}
          >
            {value}
          </Link>
        )
      case 'avatar':
      case 'image':
        return <Avatar size="sm" name={values?.name} src={value} />

      case 'spaceImage':
        return <SpaceImage size="sm" space={values} />

      case 'number':
        return value?.toLocaleString ? value.toLocaleString() : 0

      case 'html':
        return (
          <HtmlContent value={value} generateMemberPath={generateMemberPath} />
        )

      case 'postField':
        return value ? (
          <Link as={ReactLink} to={generatePostPath(values?.space, values)}>
            {JSON.parse(value)}
          </Link>
        ) : (
          ''
        )

      case 'member':
        return (
          <Badge
            variant="secondary"
            as={ReactLink}
            to={generateMemberPath(value)}
            rounded
            leadingIcon={
              <Avatar
                size="xs"
                src={value?.profilePicture?.url}
                name={value?.name}
              />
            }
          >
            {value?.name}
          </Badge>
        )

      case 'boolean':
        if (value === true)
          return <CheckIcon className="w-4 h-4 text-success-700" />
        if (value === false)
          return <XIcon className="w-4 h-4 text-danger-500" />
        return '-'

      case 'longtext':
        return (
          <div className="truncate" title={value}>
            {value}
          </div>
        )

      case 'space':
        return (
          <Link as={ReactLink} to={`/${value?.slug}`}>
            <div>{value?.name}</div>
          </Link>
        )

      case 'date':
        if (!value) return '-'
        if (format === 'fromNow')
          return (
            <div title={new Date(value).toLocaleString()}>
              {dayjs(value).fromNow()}
            </div>
          )
        if (format === 'datetime')
          return (
            <div title={new Date(value).toLocaleString()}>
              {new Date(value).toLocaleDateString()}
              {', '}
              {new Date(value).toLocaleTimeString()}
            </div>
          )
        return (
          <div title={new Date(value).toLocaleString()}>
            {new Date(value).toLocaleDateString()}
          </div>
        )

      case 'email':
        return (
          <a href={`mailto:${value}`} target="_blank">
            {value}
          </a>
        )

      case 'array':
        if (value?.join) return value?.join(', ')
        if (!value?.length) return '-'
        return value

      default:
        return value || ''
    }
  }

  const tableConfig = {
    defaultColumn: {},
    initialState: {
      hiddenColumns: memoColumns.map(column => {
        if (column.visible !== true) return column.accessor
        return null
      }),
    },
    columns: memoColumns,
    data: items,
  }

  if (CellRenderer) {
    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    tableConfig.defaultColumn.Cell = CellRenderer
  }

  const table = useTable(tableConfig, useColumnOrder)
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    table

  const loader = (
    <>
      {[...Array(10)].map((_e, i) => {
        return headerGroups.map(headerGroup => (
          // eslint-disable-next-line
          <TableComponent.Row key={i}>
            {headerGroup.headers.map((_e, j) => (
              // eslint-disable-next-line
              <TableComponent.Cell key={j} className="animate-pulse">
                <div className="h-4 bg-surface-300 rounded-full w-3/4" />
              </TableComponent.Cell>
            ))}
          </TableComponent.Row>
        ))
      })}
    </>
  )

  const columnWidths = {
    collapse: 'w-8',
    xs: 'w-12',
    sm: 'w-32',
    md: 'w-48',
    lg: 'w-64',
    xl: 'w-96',
  }

  return (
    <div className="flex flex-col p-1 space-y-3 overflow-hidden">
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 lg:col-span-4 text-basicSurface-900">
          <h3>
            {title} {totalCount ? `(${totalCount.toLocaleString()})` : ''}
          </h3>
        </div>
        <div className="flex space-x-3 col-span-12 lg:col-span-8">
          <div className="flex-grow">
            {typeof setFilters === 'function' && (
              <Input
                onChange={e => {
                  const newFilter = { ...filters }
                  newFilter.query = e.target.value || undefined
                  if (typeof setFilters === 'function') setFilters(newFilter)
                }}
                leadingIcon={<SearchIcon />}
                placeholder="Search..."
              />
            )}
          </div>
          <FieldsDropdown table={table} footer={fieldsFooter} />
          {actions ? <div className="flex-shrink-0">{actions}</div> : null}
        </div>
      </div>
      <div>
        <FieldsFilter
          fields={columns}
          setFilters={filterBy => {
            const newFilter = { ...filters, ...{ filterBy: filterBy || [] } }
            if (typeof setFilters === 'function') setFilters(newFilter)
          }}
        />
      </div>
      <Card className="overflow-hidden">
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchNextPage}
          hasMore={(hasNextPage && !isFetchingNextPage) || false}
          useWindow={false}
          getScrollParent={() => scrollRef?.current}
        >
          <TableComponent
            {...props}
            {...getTableProps({
              className: 'table-fixed',
            })}
          >
            <TableComponent.Header>
              {headerGroups.map(headerGroup => (
                // eslint-disable-next-line react/jsx-key
                <TableComponent.Row {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    // eslint-disable-next-line react/jsx-key
                    <TableComponent.HeaderCell
                      {...column.getHeaderProps({
                        className: clsx(
                          'whitespace-nowrap',
                          columnWidths[column.width] || '',
                        ),
                      })}
                    >
                      {setOrderBy && column.orderKey ? (
                        <Link
                          onClick={() => {
                            if (orderBy.column === column.orderKey) {
                              setOrderBy({
                                column: orderBy.column,
                                reverse: !orderBy.reverse,
                              })
                            } else {
                              setOrderBy({
                                column: column.orderKey,
                                reverse: true,
                              })
                            }
                          }}
                          className="flex items-center"
                        >
                          {column.render('Header')}
                          {
                            // eslint-disable-next-line no-nested-ternary
                            orderBy.column === column.orderKey ? (
                              orderBy.reverse ? (
                                <ChevronDownIcon className="mx-1 w-4 h-4 text-basicSurface-500" />
                              ) : (
                                <ChevronUpIcon className="mx-1 w-4 h-4 text-basicSurface-500" />
                              )
                            ) : null
                          }
                        </Link>
                      ) : (
                        column.render('Header')
                      )}
                    </TableComponent.HeaderCell>
                  ))}
                </TableComponent.Row>
              ))}
            </TableComponent.Header>
            <TableComponent.Body {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row)
                return (
                  // eslint-disable-next-line react/jsx-key
                  <TableComponent.Row {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <TableComponent.Cell {...cell.getCellProps()}>
                          <div
                            className={clsx(
                              cell?.column?.width !== 'collapse' &&
                                'overflow-hidden truncate',
                              columnWidths[cell?.column?.width] || '',
                            )}
                          >
                            {cell.render('Cell')}
                          </div>
                        </TableComponent.Cell>
                      )
                    })}
                  </TableComponent.Row>
                )
              })}
              {!isLoading && totalCount === 0 ? (
                <TableComponent.Row>
                  <TableComponent.Cell
                    colSpan={headerGroups?.[0].headers.length}
                  >
                    <div className="py-12 text-center">
                      Nothing to show here...
                    </div>
                  </TableComponent.Cell>
                </TableComponent.Row>
              ) : null}
              {isLoading || isFetchingNextPage ? loader : null}
            </TableComponent.Body>
          </TableComponent>
        </InfiniteScroll>
      </Card>
    </div>
  )
}
